/**
 * Utility functions for session management and API compatibility
 */

/**
 * Maps sessionId to conversationId for API compatibility
 * @param {string | null} sessionId - The internal session identifier
 * @return {string | null} conversationId - The API-compatible conversation identifier
 */
export const mapSessionToConversationId = (
  sessionId: string | null
): string | null => {
  if (!sessionId) return null;

  // Extract the unique part from session ID for API compatibility
  // sessionId format: "session_{tabId}_{uniqueId}"
  const parts = sessionId.split('_');
  if (parts.length >= 3) {
    // Return the unique identifier part for API calls
    return parts.slice(2).join('_');
  }

  // Fallback to full sessionId if format is unexpected
  return sessionId;
};

/**
 * Gets the current tab ID from sessionStorage
 * @return {string} tabId - The current browser tab identifier
 */
export const getCurrentTabId = (): string => {
  let tabId = sessionStorage.getItem('tabId');

  if (!tabId) {
    tabId = Date.now().toString() + Math.random().toString(36).substr(2, 9);
    sessionStorage.setItem('tabId', tabId);
  }

  return tabId;
};

/**
 * Checks if the current session belongs to the active tab
 * @param {string | null} sessionId - The session identifier to check
 * @return {boolean} boolean - Whether the session belongs to the current tab
 */
export const isCurrentTabSession = (sessionId: string | null): boolean => {
  if (!sessionId) return false;

  const currentTabId = getCurrentTabId();
  return sessionId.includes(`session_${currentTabId}_`);
};
